/**
 * Membership billing vocabulary and the access predicates derived from it.
 *
 * Mirrors `policy-versions.ts`: enums and predicates in one DOM-free module that both the client
 * lock gate and the server flow gate import, so the two cannot drift on what "locked" means.
 */

import { z } from 'zod';

import { MembershipPlanIdEnum, planRequiresPayment } from './membership-plans.js';

/**
 * Plan identity now lives in `membership-plans.ts`, which is also where a new plan is added. These
 * two names are kept here because most of the codebase imports them from `@elara/domain/subscription`
 * alongside the status enum, and because the predicates below are the reason plans exist at all.
 *
 * Comped members get a real Stripe subscription on the $0 price, so they share the same billing
 * model — and therefore the same lock predicate — as paying members.
 */
export const SubscriptionPlanEnum = MembershipPlanIdEnum;
export type SubscriptionPlan = z.infer<typeof SubscriptionPlanEnum>;

/**
 * Stripe's statuses, plus a local `none` for a profile whose subscription does not exist yet.
 *
 * All eight of Stripe's own values are listed, `paused` included — the SDK's `Subscription.Status`
 * union also ends in `OtherString`, so a status we do not model can arrive. Parse Stripe's value
 * rather than trusting it: an unmodelled string must not reach a profile write, or the write throws
 * and leaves the member's billing state stale.
 */
export const SubscriptionStatusEnum = z.enum([
  'none',
  'incomplete',
  'incomplete_expired',
  'active',
  'trialing',
  'past_due',
  'paused',
  'canceled',
  'unpaid',
]);
export type SubscriptionStatus = z.infer<typeof SubscriptionStatusEnum>;

/**
 * The longest free trial an operator may put on an invite code.
 *
 * Stripe accepts up to 730 days; this is far tighter on purpose. A trial is minted by hand, one
 * code at a time, and a stray digit should not hand somebody a year of membership for free.
 */
export const MAX_INVITE_TRIAL_DAYS = 90;

/** Free days before the first charge, as stored on an invite code and copied onto the profile. */
export const TrialDaysSchema = z.number().int().positive();

/** Server-authoritative: written only by `CreateUserProfile` and the Stripe webhook. */
export const MembershipSubscriptionSchema = z.object({
  plan: SubscriptionPlanEnum.describe('Which membership this account is on'),
  status: SubscriptionStatusEnum.describe('Stripe status; `none` before a subscription exists'),
  stripeSubscriptionId: z.string().nullish().describe('Stripe subscription id (`sub_…`)'),
  currentPeriodEnd: z.string().nullish().describe('ISO timestamp the period ends'),
  cancelAtPeriodEnd: z.boolean().nullish().describe('Cancelled; access runs to `currentPeriodEnd`'),
  trialDays: TrialDaysSchema.nullish().describe(
    'Free trial granted by the invite code, on `plan` only. Copied at signup; the projection leaves it alone',
  ),
});
export type MembershipSubscription = z.infer<typeof MembershipSubscriptionSchema>;

/**
 * The free trial this member was invited with, if `planId` is the plan it was granted on.
 *
 * The trial belongs to the invite, not to a plan: an operator chose "annual, with 14 days free"
 * for this person, and that offer says nothing about any other plan. So choosing a different plan
 * at the payment step forfeits the trial rather than carrying it across — a fortnight free means
 * something very different on a monthly plan than on an annual one, and nobody chose that.
 *
 * Says nothing about whether the member is still ELIGIBLE (one trial per customer); that is
 * Stripe's to answer, and callers ask it separately.
 */
export function invitedTrialDays(
  subscription: Pick<MembershipSubscription, 'plan' | 'trialDays'> | undefined,
  planId: string | undefined,
): number | undefined {
  if (!subscription?.trialDays || subscription.plan !== planId) return;
  return subscription.trialDays;
}

/**
 * `past_due` is inside the gate on purpose: Stripe is still retrying the charge and emailing the
 * member. It becomes `unpaid` or `canceled` once retries are exhausted, and those lock.
 */
const ACTIVE_STATUSES: ReadonlySet<SubscriptionStatus> = new Set(['active', 'trialing', 'past_due']);

/**
 * Whether a status entitles its holder to the app — the same set `isSubscriptionLocked` gates on,
 * exposed for the callers that hold a Stripe status rather than a profile.
 *
 * Notably `findLiveSubscription`, which has to rank several of a customer's subscriptions and must
 * prefer the one that entitles: an `incomplete` subscription created after an `active` one is an
 * abandoned attempt, and letting recency win there hides a membership somebody paid for.
 */
export function isEntitlingStatus(status: SubscriptionStatus | undefined): status is SubscriptionStatus {
  return status !== undefined && ACTIVE_STATUSES.has(status);
}

/** Narrow slice of a profile, `Pick`ed so a renamed schema field breaks here instead of drifting. */
export interface SubscriptionFields {
  subscription?: Pick<MembershipSubscription, 'plan' | 'status'>;
}

/**
 * What this member may do, as ONE answer.
 *
 * Access used to be two overlapping booleans — "is locked" and "needs a first payment" — read
 * independently by the client gate, the server flows and the route guard. Every reader had to
 * combine them correctly, and each one that got it wrong got it wrong differently: a member who
 * owed a payment was sent to onboarding, a trial with no card read as entitled, and a member
 * mid-signup fell through a gap where both booleans said "fine". A single exhaustive answer removes
 * the combining step, and with it the class of bug.
 *
 * The four cases are the four things that can actually be true, and each one names its own remedy:
 *
 *   not_applicable       no subscription record. Billing does not apply — an account predating the
 *                        rollout, or an environment with no Stripe. Access is granted.
 *   entitled             a live membership: active, trialing, or past_due (Stripe is still retrying
 *                        and emailing them; access continues until it gives up).
 *   needs_subscription   no live subscription to revive: never paid, or cancelled. The remedy is
 *                        the card form, which creates a NEW subscription. Cancelled is here and not
 *                        under `lapsed` because Stripe's portal cannot bring a cancelled
 *                        subscription back — it manages live ones — so sending a cancelled member
 *                        there left them with no way to re-subscribe.
 *   lapsed              has a subscription Stripe can still revive: unpaid (pay the open invoice)
 *                        or paused (resume). The remedy is the billing portal, NOT the card form,
 *                        which would mint a second subscription beside the one that needs fixing.
 *   ended                a comped membership that is over. No self-serve remedy exists: the card
 *                        form would charge someone who owes nothing, and the portal cannot revive
 *                        a cancelled subscription. The screen says to get in touch.
 */
export type MembershipAccess =
  | { kind: 'not_applicable' }
  | { kind: 'entitled'; status: SubscriptionStatus }
  | {
      kind: 'needs_subscription';
      plan: SubscriptionPlan;
      /**
       * True for a member who held a membership and cancelled it, false for one who never completed
       * a first payment. Same remedy either way, but the screen should not say "Start" to someone
       * who is coming back.
       */
      returning: boolean;
    }
  | { kind: 'lapsed'; plan: SubscriptionPlan; status: SubscriptionStatus }
  /**
   * A comped membership that is over. Nothing to pay and nothing Stripe can revive, so neither the
   * card form nor the portal is a remedy — the only route back is a person. Reached when an operator
   * cancels a comp in the Dashboard, which is how a comp gets revoked, so it will happen.
   */
  | { kind: 'ended'; plan: SubscriptionPlan; status: SubscriptionStatus };

/**
 * Statuses with nothing left to revive. A new subscription is the only way forward.
 *
 * `canceled` sits alongside the never-paid statuses on purpose. Stripe marks a subscription
 * `canceled` when the member cancels, when dunning gives up (with the Dashboard's default retry
 * setting), or when a trial ends with no card — and in every case the object is terminal. Whether a
 * member has paid before does not change what has to happen next.
 */
const NEEDS_SUBSCRIPTION_STATUSES: ReadonlySet<SubscriptionStatus> = new Set([
  'none',
  'incomplete',
  'incomplete_expired',
  'canceled',
]);

/**
 * The single decision every gate reads — client lock, server flows, route guard.
 *
 * An absent `subscription` is NOT locked: every member predating the billing rollout has none, so
 * locking on absence would deny the whole existing member base.
 *
 * A plan id this build does not know is treated as PAID, which is the direction with no bad case: a
 * member naming a plan a later release removed is offered the card form rather than left with no
 * exit. Only their status decides whether they need it.
 */
export function membershipAccess(profile: SubscriptionFields): MembershipAccess {
  const { subscription } = profile;
  if (!subscription) return { kind: 'not_applicable' };

  const { plan, status } = subscription;
  if (isEntitlingStatus(status)) return { kind: 'entitled', status };

  if (planRequiresPayment(plan)) {
    if (NEEDS_SUBSCRIPTION_STATUSES.has(status)) {
      return { kind: 'needs_subscription', plan, returning: status === 'canceled' };
    }
    // What is left is a subscription Stripe can revive: unpaid, or paused.
    return { kind: 'lapsed', plan, status };
  }

  // A comped plan. Paused is the one state the portal can still act on (resume); everything else
  // means the comp is over, and no self-serve step exists — provisioning never records a comped
  // member as owing payment, precisely so they cannot be sent to the card form.
  if (status === 'paused') return { kind: 'lapsed', plan, status };
  return { kind: 'ended', plan, status };
}

/**
 * Narrow a status string reported by Stripe to one this app models.
 *
 * Stripe's SDK types `Subscription.Status` as its eight known values plus `OtherString`, so a
 * status we have never seen can arrive from a real API response or webhook. Returning `undefined`
 * rather than throwing keeps that decision at the call site: a profile write must not blow up
 * mid-signup, and a webhook must not fail its delivery, over a string we simply do not recognise.
 *
 * Callers should treat `undefined` as "leave the billing state alone and alert" — never as a
 * licence to invent a status, and never as grounds to grant access.
 */
export function parseSubscriptionStatus(raw: string): SubscriptionStatus | undefined {
  const parsed = SubscriptionStatusEnum.safeParse(raw);
  return parsed.success ? parsed.data : undefined;
}
